import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../../api';
import { useAuth } from '../../context/AuthContext';
import TopBar from '../../Components/TopBar';
import Navbar from '../../Components/Navbar';
import Footer from '../../Components/Footer';
import './CourseDetailsPage.css';

const formatDate = value => {
  if (!value) return 'TBA';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value).slice(0, 10);
  return date.toLocaleDateString();
};

const isEnrollIntake = intake => {
  if (!intake || intake.status !== 'open') return false;
  if (!intake.end_date) return true;
  const end = new Date(intake.end_date);
  const today = new Date();
  end.setHours(0, 0, 0, 0);
  today.setHours(0, 0, 0, 0);
  return end >= today;
};

const LEVEL_OF_STUDIES_OPTIONS = [
  { value: 'bachelors_degree', label: "Bachelor's degree" },
  { value: 'masters_degree', label: "Master's Degree" },
  { value: 'university_student_last_level', label: 'University Student at the last level' },
];

const EMPLOYMENT_STATUS_OPTIONS = [
  { value: 'employed', label: 'Employed' },
  { value: 'unemployed', label: 'Unemployed' },
  { value: 'student', label: 'Student' },
  { value: 'business_owner', label: 'Business Owner' },
];

const TRAINING_TIME_OPTIONS = [
  { value: 'day_program', label: 'Day Program (Monday to Friday, 9:00 AM to 12:00 PM)' },
  { value: 'weekend_program', label: 'Weekend Program (Saturday and Sunday, 9:00 AM to 1:00 PM)' },
  { value: 'evening_program', label: 'Evening Program (Monday to Friday, 6:00 PM to 8:00 PM)' },
  { value: 'online_program', label: 'Online (Monday to Friday, 6:00 PM to 8:00 PM)' },
  { value: 'day_afternoon_session', label: 'Day Afternoon Session (Monday to Friday, 2:00 PM to 4:00 PM)' },
];

const SKILL_LEVEL_OPTIONS = [
  { value: 'beginner', label: 'Beginner' },
  { value: 'intermediate', label: 'Intermediate' },
  { value: 'advanced', label: 'Advanced' },
];

const PAYMENT_METHOD_OPTIONS = [
  { value: 'momo_pay', label: 'MOMO PAY (1708263) ICB-FINANCE EXPERTS Ltd' },
  {
    value: 'bank_transfer',
    label: 'Transfer to BK Account 100154516241 under ICB-FINANCE EXPERTS Ltd',
  },
];

const CourseDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  const selectedIntakeFromQuery = new URLSearchParams(location.search).get('intake');

  const [course, setCourse] = useState(null);
  const [courseIntakeId, setCourseIntakeId] = useState('');
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [levelOfStudies, setLevelOfStudies] = useState('');
  const [employmentStatus, setEmploymentStatus] = useState('');
  const [favorableTrainingTime, setFavorableTrainingTime] = useState('');
  const [accountingSkillLevel, setAccountingSkillLevel] = useState('');
  const [excelSkillLevel, setExcelSkillLevel] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('');
  const [notes, setNotes] = useState('');
  const [paymentProof, setPaymentProof] = useState(null);

  const userFullName = user?.fullName || user?.name || '';
  const userEmail = user?.email || '';

  useEffect(() => {
    if (userFullName && !fullName) {
      setFullName(userFullName);
    }
  }, [userFullName, fullName]);

  useEffect(() => {
    if (userEmail && !email) {
      setEmail(userEmail);
    }
  }, [userEmail, email]);

  useEffect(() => {
    if (user?.phone && !phone) {
      setPhone(user.phone);
    }
  }, [user, phone]);

  useEffect(() => {
    loadCourse();
  }, [id]);

  useEffect(() => {
    const query = new URLSearchParams(location.search);
    if (query.get('enroll') === '1') {
      setTimeout(() => {
        const el = document.getElementById('course-enroll-form');
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 150);
    }
  }, [location.search, course]);

  const loadCourse = async () => {
    const res = await api.get(`/courses/${id}`);
    setCourse(res.data);
    const openIntakes = (res.data?.intakes || []).filter(isEnrollIntake);
    if (!openIntakes.length) return;

    const queryIntake = selectedIntakeFromQuery
      ? openIntakes.find(i => String(i.id) === String(selectedIntakeFromQuery))
      : null;

    setCourseIntakeId(String((queryIntake || openIntakes[0]).id));
  };

  const enroll = async e => {
    e.preventDefault();

    if (!paymentProof) {
      toast.error('Payment proof is required');
      return;
    }

    if (!courseIntakeId) {
      toast.error('Please select an intake/session');
      return;
    }

    const form = new FormData();
    form.append('course_id', id);
    form.append('course_intake_id', courseIntakeId);
    form.append('full_name', fullName);
    form.append('email', email);
    form.append('phone_number', phone);
    form.append('level_of_studies', levelOfStudies);
    form.append('employment_status', employmentStatus);
    form.append('favorable_training_time', favorableTrainingTime);
    form.append('accounting_skill_level', accountingSkillLevel);
    form.append('excel_skill_level', excelSkillLevel);
    form.append('payment_method', paymentMethod);
    form.append('additional_notes', notes);
    form.append('payment_proof', paymentProof);

    try {
      await api.post('/enrollments', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      toast.success('Enrollment submitted successfully');
      navigate('/student/dashboard');
    } catch (err) {
      const validationErrors = err.response?.data?.errors;
      const firstValidationError = validationErrors
        ? Object.values(validationErrors)?.[0]?.[0]
        : null;
      const msg = firstValidationError || err.response?.data?.message || 'Enrollment failed';
      toast.error(msg);
    }
  };

  if (!course) return <div className="p-5">Loading...</div>;

  const availableIntakes = (course.intakes || []).filter(isEnrollIntake);
  const hasSelectedIntakeFromList = availableIntakes.some(i => String(i.id) === String(courseIntakeId));
  const isIntakeLocked = Boolean(selectedIntakeFromQuery && hasSelectedIntakeFromList);
  const selectedIntake = availableIntakes.find(i => String(i.id) === String(courseIntakeId));

  return (
    <>
      <TopBar />
      <main className="page-about">
        <div className="container">
          <Navbar />
        </div>

        <section className="lms-course-details-hero">
          <div className="lms-hero-shell">
            <div className="lms-hero-left">
              <p className="lms-hero-kicker">Course Information</p>
              <h1 className="lms-hero-title">
                <span className="lms-hero-title-blue">{course.title}</span>
              </h1>
              <p className="lms-hero-description">{course.description}</p>
              <div className="lms-hero-actions">
                <a href="#course-enroll-form" className="lms-hero-btn lms-hero-btn-primary">
                  Enroll Now
                </a>
                <a href="#course-enroll-form" className="lms-hero-btn lms-hero-btn-secondary">
                  Contact Team
                </a>
              </div>
            </div>

            <div className="lms-hero-right-wrap">
              <div className="lms-hero-right-card">
                <div className="lms-hero-card-head">
                  <span className="lms-hero-card-badge">ICB</span>
                  <strong>{course.category || 'Professional Course'}</strong>
                </div>

                <div className="lms-hero-info-row">
                  <span className="lms-hero-info-label">Trainer</span>
                  <span className="lms-hero-info-value">{course.trainer || 'To be announced'}</span>
                </div>
                <div className="lms-hero-info-row">
                  <span className="lms-hero-info-label">Duration</span>
                  <span className="lms-hero-info-value">{course.duration || 'TBA'}</span>
                </div>
                <div className="lms-hero-info-row">
                  <span className="lms-hero-info-label">Schedule</span>
                  <span className="lms-hero-info-value">{course.schedule || 'TBA'}</span>
                </div>
                <div className="lms-hero-info-row">
                  <span className="lms-hero-info-label">Tuition Fee</span>
                  <span className="lms-hero-info-value">
                    {course.fee_currency || 'RWF'} {course.fee}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </section>

        <div className="container">
          <div className="py-3 lms-course-details-wrap">
            <div className="lms-course-flow">
              <section className="lms-course-details-card">
                <h5 className="mb-3 fw-bold" style={{ color: '#0f2350' }}>What you will learn</h5>
                {(course.outline || []).length ? (
                  <ul className="lms-course-outline">
                    {(course.outline || []).map((item, i) => (
                      <li key={`${item}-${i}`}>{item}</li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-muted mb-0">Detailed outline will be shared before class starts.</p>
                )}
              </section>

              <section className="lms-course-enroll-card" id="course-enroll-form">
                  <div className="lms-enroll-card-header">
                    <h5>Enroll now</h5>
                    <p>Secure your seat by completing the form below.</p>
                  </div>

                  <form onSubmit={enroll}>
                    {/* Personal info */}
                    <div className="lms-enroll-section">
                      <div className="lms-enroll-section-title">Personal information</div>
                      <div className="row g-3">
                        <div className="col-12">
                          <label className="form-label">Full names (as on ID or Passport)</label>
                          <input
                            required
                            className="form-control"
                            value={fullName}
                            placeholder="Your full name"
                            onChange={e => setFullName(e.target.value)}
                          />
                        </div>
                        <div className="col-sm-6">
                          <label className="form-label">Email address</label>
                          <input
                            required
                            type="email"
                            className="form-control"
                            value={email}
                            placeholder="name@email.com"
                            onChange={e => setEmail(e.target.value)}
                          />
                        </div>
                        <div className="col-sm-6">
                          <label className="form-label">Telephone number</label>
                          <input
                            required
                            className="form-control"
                            placeholder="+250 7xx xxx xxx"
                            value={phone}
                            onChange={e => setPhone(e.target.value)}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Session */}
                    <div className="lms-enroll-section">
                      <div className="lms-enroll-section-title">Intake & session</div>
                      <div className="row g-3">
                        <div className="col-12">
                          <label className="form-label">Intake / Session</label>
                          {isIntakeLocked && selectedIntake ? (
                            <div className="lms-intake-locked">
                              ✓ {selectedIntake.name}
                              {(selectedIntake.start_date || selectedIntake.end_date) &&
                                ` · ${formatDate(selectedIntake.start_date)} – ${formatDate(selectedIntake.end_date)}`}
                            </div>
                          ) : (
                            <select
                              required
                              className="form-select"
                              value={courseIntakeId}
                              onChange={e => setCourseIntakeId(e.target.value)}
                            >
                              <option value="">Select intake / session</option>
                              {availableIntakes.map(i => (
                                <option key={i.id} value={i.id}>
                                  {i.name}
                                  {i.start_date || i.end_date
                                    ? ` (${formatDate(i.start_date)} – ${formatDate(i.end_date)})`
                                    : ''}
                                </option>
                              ))}
                            </select>
                          )}
                        </div>
                        <div className="col-12">
                          <label className="form-label">Favorable training time</label>
                          <select
                            required
                            className="form-select"
                            value={favorableTrainingTime}
                            onChange={e => setFavorableTrainingTime(e.target.value)}
                          >
                            <option value="">Select preferred schedule</option>
                            {TRAINING_TIME_OPTIONS.map(o => (
                              <option key={o.value} value={o.value}>{o.label}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>

                    {/* Academic profile */}
                    <div className="lms-enroll-section">
                      <div className="lms-enroll-section-title">Academic profile</div>
                      <div className="row g-3">
                        <div className="col-12">
                          <label className="form-label">Level of studies</label>
                          <select
                            required
                            className="form-select"
                            value={levelOfStudies}
                            onChange={e => setLevelOfStudies(e.target.value)}
                          >
                            <option value="">Select level</option>
                            {LEVEL_OF_STUDIES_OPTIONS.map(o => (
                              <option key={o.value} value={o.value}>{o.label}</option>
                            ))}
                          </select>
                        </div>
                        <div className="col-12">
                          <label className="form-label">Employment status</label>
                          <select
                            required
                            className="form-select"
                            value={employmentStatus}
                            onChange={e => setEmploymentStatus(e.target.value)}
                          >
                            <option value="">Select status</option>
                            {EMPLOYMENT_STATUS_OPTIONS.map(o => (
                              <option key={o.value} value={o.value}>{o.label}</option>
                            ))}
                          </select>
                        </div>
                        <div className="col-sm-6">
                          <label className="form-label">Accounting skill level</label>
                          <select
                            required
                            className="form-select"
                            value={accountingSkillLevel}
                            onChange={e => setAccountingSkillLevel(e.target.value)}
                          >
                            <option value="">Select level</option>
                            {SKILL_LEVEL_OPTIONS.map(o => (
                              <option key={o.value} value={o.value}>{o.label}</option>
                            ))}
                          </select>
                        </div>
                        <div className="col-sm-6">
                          <label className="form-label">Excel skill level</label>
                          <select
                            required
                            className="form-select"
                            value={excelSkillLevel}
                            onChange={e => setExcelSkillLevel(e.target.value)}
                          >
                            <option value="">Select level</option>
                            {SKILL_LEVEL_OPTIONS.map(o => (
                              <option key={o.value} value={o.value}>{o.label}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>

                    {/* Payment */}
                    <div className="lms-enroll-section">
                      <div className="lms-enroll-section-title">Payment</div>
                      <div className="row g-3">
                        <div className="col-12">
                          <label className="form-label">Payment method</label>
                          <select
                            required
                            className="form-select"
                            value={paymentMethod}
                            onChange={e => setPaymentMethod(e.target.value)}
                          >
                            <option value="">Select payment method</option>
                            {PAYMENT_METHOD_OPTIONS.map(o => (
                              <option key={o.value} value={o.value}>{o.label}</option>
                            ))}
                          </select>
                          <div className="lms-payment-notice">
                            ⚠ Pay full amount upon registration, or 50% deposit = RWF 88,500. Call 0788393784 for assistance.
                          </div>
                        </div>
                        <div className="col-12">
                          <label className="form-label">Upload proof of payment</label>
                          <input
                            type="file"
                            required
                            accept=".pdf,.png,.jpg,.jpeg"
                            className="form-control"
                            onChange={e => setPaymentProof(e.target.files?.[0])}
                          />
                          <div className="form-text">Accepted: PDF, PNG, JPG · Max 5 MB</div>
                        </div>
                      </div>
                    </div>

                    {/* Notes */}
                    <div className="lms-enroll-section" style={{ marginBottom: '0.55rem' }}>
                      <div className="lms-enroll-section-title">Additional notes (optional)</div>
                      <textarea
                        className="form-control"
                        placeholder="Any additional information relevant to your enrollment…"
                        value={notes}
                        onChange={e => setNotes(e.target.value)}
                        rows={3}
                      />
                    </div>

                    <button className="lms-submit-btn" type="submit">
                      Submit enrollment →
                    </button>
                  </form>
              </section>
            </div>
          </div>
        </div>
        <Footer />
      </main>
    </>
  );
};

export default CourseDetailsPage;
